<template>
  <div
    :id="`${source}_marketing_campaigns_analytics`"
    class="flex flex-col gap-2 pb-3"
  >
    <div class="flex flex-row items-center justify-between gap-4 mb-4">
      <div class="flex flex-row items-center gap-4">
        <DateTimeFilter
          source="marketing_campaigns_analytics"
          parent-format="YYYY-MM-DD"
          @update:dateRange="handleDateRangeUpdate"
        />
        <a-select
          v-model:value="selectedChannelType"
          placeholder="Channel Type"
          class="w-[200px]"
          allow-clear
          @change="handleChannelTypeChange"
        >
          <a-select-option value="all">All</a-select-option>
          <a-select-option value="email">Email</a-select-option>
          <a-select-option value="whatsapp">WhatsApp</a-select-option>
        </a-select>
      </div>
      
      <CustomDropdown maxHeight="200px" gap="10px" position="end">
        <template #trigger>
          <div
            class="flex items-center justify-center p-2 rounded-lg bg-[--white-opacity-2] border_8 cursor-pointer hover:bg-[--white-opacity-4] hover:border_12"
          >
            <ThemeImage
              class="size-4 cursor-pointer"
              :icon="imageNames.MORE_VERTICAL"
              alt="more_options"
            />
          </div>
        </template>

        <template #default>
          <div
            class="flex items-center flex-row gap-2 px-3 py-2 rounded-lg dropdown_item cursor-pointer"
            @click="handleExportCSV"
          >
            <ThemeImage
              class="size-4"
              :icon="imageNames.EXPORT_GREY"
              alt="export_icon"
            />
            <span class="proxima_nova text-sm">Export CSV</span>
          </div>
        </template>
      </CustomDropdown>
    </div>

    <div class="flex flex-col gap-8 mb-3">
      <MarketingAnalyticsGrid :channel-type="selectedChannelType" />

      <div
        v-for="(component, index) in visibleComponents"
        :key="`${component.id}_component_${index}`"
        class="flex flex-col gap-3"
      >
        <span class="text-lg font-semibold">{{ component.text }}</span>
        <component
          :is="component.component"
          :source="source"
          :activity-type="component.id"
        />
      </div>
    </div>

    <BulkExportModal
      source="marketing_campaigns_bulk"
      :visible="showExportModal"
      title="Export Marketing Campaigns Analytics"
      description="All campaigns analytics will be exported to your email"
      :selectCount="0"
      @update:visible="val => showExportModal = val"
      @confirm="handleExportConfirm"
    />
  </div>
</template>

<script setup>
import { computed, ref } from "vue";
import { useStore } from "vuex";
import EmailAnalytics from "@/src/views/analytics/v2/marketing_campaign_analytics/email/EmailAnalytics.vue";
import WhatsAppAnalytics from "@/src/views/analytics/v2/marketing_campaign_analytics/whatsapp/WhatsAppAnalytics.vue";
import MarketingAnalyticsGrid from "@/src/views/analytics/v2/marketing_campaign_analytics/MarketingAnalyticsGrid.vue";
import DateTimeFilter from "@/src/components/DateTimeFilter.vue";
import CustomDropdown from "@/src/components/CustomDropdown.vue";
import ThemeImage from "@/src/components/ThemeImage.vue";
import BulkExportModal from "@/src/components/custom_table_components/BulkExportModal.vue";
import { imageNames } from "@/src/constants/imageConstants.js";
import { useCampaignExport } from "@/src/composables/useCampaignExport.js";

const props = defineProps({
  source: { type: String, required: false },
});

const store = useStore();
const selectedChannelType = ref("all");

const { 
  showExportModal, 
  openExportModal, 
  exportBulkCampaigns 
} = useCampaignExport();

const components = ref([
  {
    id: "email_activity_analytics",
    text: "Email",
    component: EmailAnalytics,
    show: true,
  },
  {
    id: "whatsapp_activity_analytics",
    text: "WhatsApp",
    component: WhatsAppAnalytics,
    show: true,
  },
]);

const visibleComponents = computed(() => {
  if (!selectedChannelType.value || selectedChannelType.value === "all") {
    return components.value.filter((component) => component.show);
  }

  return components.value.filter((component) => {
    if (!component.show) return false;

    if (selectedChannelType.value === "email") {
      return component.id === "email_activity_analytics";
    } else if (selectedChannelType.value === "whatsapp") {
      return component.id === "whatsapp_activity_analytics";
    }

    return true;
  });
});

const handleDateRangeUpdate = (value) => {
  store.dispatch("analytics/updateRange", value);
};

const handleChannelTypeChange = (value) => {
  selectedChannelType.value = value;
  const currentFilters = store.getters["analytics/getFilters"] || {};
  if (value && value !== "all") {
    store.dispatch("analytics/updateFilters", {
      ...currentFilters,
      channel_type: [value],
    });
  } else {
    const { channel_type, ...restFilters } = currentFilters;
    store.dispatch("analytics/updateFilters", restFilters);
  }
};

const handleExportCSV = () => {
  openExportModal();
};

const handleExportConfirm = async ({ email }) => {
  const dateRange = store.getters["analytics/getRange"];
  
  await exportBulkCampaigns({
    email,
    dateRange,
    selectedChannelType: selectedChannelType.value,
  });
};
</script>